import React, { useState, useCallback, useMemo } from 'react';
import { Card, Button, message, Spin, Tabs, Typography, Space, Row, Col, Statistic, Progress, List, Select, Table, Input, Calendar, Badge, Tag, Timeline, Tooltip, Divider, Alert } from 'antd';
import { DatabaseOutlined, CarOutlined, EnvironmentOutlined, ClockCircleOutlined, TeamOutlined, AimOutlined, CalendarOutlined, FieldTimeOutlined, SearchOutlined, ArrowRightOutlined, InfoCircleOutlined, QuestionCircleOutlined, DownloadOutlined, FileTextOutlined, WarningOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAppContext } from '../../libs/contextLib';
import { GTFSProcessor } from '../../model/gtfs';
import { buildGtfsZip } from '../../libs/exportLib';
import { values } from 'lodash/object';
import { keyBy } from 'lodash/collection';
import { BusRoute } from '../../model/busRoute';
import { routeModelData, stopModelData, scheduleModelData, driverShiftModelData, vehicleShiftModelData, shiftBatModelData, deadrunModelData } from '../../services/ModelService';
import dayjs from 'dayjs';
import { Column, Line } from '@ant-design/plots';

const { Title, Text } = Typography;

// Internal components
const GTFSOverview = ({ gtfsData }) => {
    if (!gtfsData) return null;
    
    const stats = {
        routes: gtfsData.routes?.length || 0,
        trips: gtfsData.trips?.length || 0,
        stops: gtfsData.stops?.length || 0,
        agencies: gtfsData.agency?.length || 0,
        shapes: gtfsData.shapes?.length || 0,
        calendars: gtfsData.calendar?.length || 0,
        stopTimes: gtfsData.stopTimes?.length || 0
    };

    const routeTypes = gtfsData.routes?.reduce((acc, route) => {
        const type = route.route_type || 'unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, {}) || {};

    // Calculate TODIS validation issues
    const todisIssues = [];
    
    // Check trip headsigns (21 char limit)
    const longHeadsigns = gtfsData.trips
        .filter(t => t.trip_headsign && t.trip_headsign.length > 21)
        .map(t => ({
            trip_id: t.trip_id,
            headsign: t.trip_headsign,
            length: t.trip_headsign.length
        }));
    
    if (longHeadsigns.length > 0) {
        todisIssues.push({
            type: 'error',
            message: `${longHeadsigns.length} trips have headsigns exceeding 21 characters`,
            examples: longHeadsigns.slice(0, 3).map(t => 
                `Trip ${t.trip_id}: "${t.headsign}" (${t.length} chars)`
            )
        });
    }

    // Check stop names (52 char limit)
    const longStopNames = gtfsData.stops
        .filter(s => s.stop_name && s.stop_name.length > 52)
        .map(s => ({
            stop_id: s.stop_id,
            name: s.stop_name,
            length: s.stop_name.length
        }));

    if (longStopNames.length > 0) {
        todisIssues.push({
            type: 'error',
            message: `${longStopNames.length} stops have names exceeding 52 characters`,
            examples: longStopNames.slice(0, 3).map(s => 
                `Stop ${s.stop_id}: "${s.name}" (${s.length} chars)`
            )
        });
    }

    // Check for missing required fields
    const tripsWithoutHeadsign = gtfsData.trips.filter(t => !t.trip_headsign).length;
    if (tripsWithoutHeadsign > 0) {
        todisIssues.push({
            type: 'warning',
            message: `${tripsWithoutHeadsign} trips missing headsigns`,
            examples: []
        });
    }

    const stopsWithoutNames = gtfsData.stops.filter(s => !s.stop_name).length;
    if (stopsWithoutNames > 0) {
        todisIssues.push({
            type: 'warning',
            message: `${stopsWithoutNames} stops missing names`,
            examples: []
        });
    }

    // Check for invalid times
    const invalidTimes = gtfsData.stopTimes.filter(st => 
        st.arrival_time && !st.arrival_time.match(/^\d{2}:\d{2}:\d{2}$/) ||
        st.departure_time && !st.departure_time.match(/^\d{2}:\d{2}:\d{2}$/)
    ).length;

    if (invalidTimes > 0) {
        todisIssues.push({
            type: 'error',
            message: `${invalidTimes} stop times with invalid time format (must be HH:MM:SS)`,
            examples: []
        });
    }

    return (
        <div>
            {todisIssues.length > 0 && (
                <Card
                    title={
                        <Space>
                            <Text strong style={{ fontSize: '16px', color: '#cf1322' }}>TODIS Validation Issues</Text>
                            <Tag color="red">{todisIssues.length} issues found</Tag>
                        </Space>
                    }
                    style={{ borderColor: '#ffccc7' }}
                >
                    <List
                        dataSource={todisIssues}
                        renderItem={issue => (
                            <List.Item>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Space>
                                        <Tag color={issue.type === 'error' ? 'red' : 'orange'}>{issue.type.toUpperCase()}</Tag>
                                        <Text strong>{issue.message}</Text>
                                    </Space>
                                    {issue.examples.length > 0 && (
                                        <ul style={{ margin: '8px 0 0 24px', color: '#666' }}>
                                            {issue.examples.map((example, i) => (
                                                <li key={i}>{example}</li>
                                            ))}
                                        </ul>
                                    )}
                                </Space>
                            </List.Item>
                        )}
                    />
                </Card>
            )}

            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Title level={4}>GTFS Data Overview</Title>
                        <Text>
                            GTFS (General Transit Feed Specification) is a standardized format for public transportation schedules and associated geographic information.
                            Below is a summary of your transit network data.
                        </Text>
                    </Space>
                </Card>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card>
                            <Tooltip title="Total number of unique bus routes in your network">
                                <Statistic
                                    title={<Space>Total Routes <InfoCircleOutlined /></Space>}
                                    value={stats.routes}
                                    prefix={<CarOutlined />}
                                />
                            </Tooltip>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card>
                            <Tooltip title="Total number of bus stops across all routes">
                                <Statistic
                                    title={<Space>Total Stops <InfoCircleOutlined /></Space>}
                                    value={stats.stops}
                                    prefix={<EnvironmentOutlined />}
                                />
                            </Tooltip>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card>
                            <Tooltip title="Total number of scheduled trips across all routes">
                                <Statistic
                                    title={<Space>Total Trips <InfoCircleOutlined /></Space>}
                                    value={stats.trips}
                                    prefix={<ClockCircleOutlined />}
                                />
                            </Tooltip>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card>
                            <Tooltip title="Number of transit agencies in the network">
                                <Statistic
                                    title={<Space>Total Agencies <InfoCircleOutlined /></Space>}
                                    value={stats.agencies}
                                    prefix={<TeamOutlined />}
                                />
                            </Tooltip>
                        </Card>
                    </Col>
                </Row>

                <Card 
                    title={
                        <Space>
                            Route Types Distribution
                            <Tooltip title="Shows how many routes of each type exist in your network. Route types indicate the mode of transport (e.g., regular bus, express bus, etc.)">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </Space>
                    }
                >
                    <Row gutter={[16, 16]}>
                        {Object.entries(routeTypes).map(([type, count]) => (
                            <Col key={type} xs={24} sm={12} md={8}>
                                <Card size="small">
                                    <Statistic
                                        title={type.charAt(0).toUpperCase() + type.slice(1)}
                                        value={count}
                                        suffix={`/ ${stats.routes}`}
                                    />
                                    <Progress
                                        percent={Math.round((count / stats.routes) * 100)}
                                        size="small"
                                        showInfo={false}
                                    />
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card>

                <Card 
                    title={
                        <Space>
                            Data Coverage
                            <Tooltip title="Overview of the data completeness in your GTFS feed">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </Space>
                    }
                >
                    <List
                        grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4 }}
                        dataSource={[
                            { title: 'Shapes', value: stats.shapes, icon: <AimOutlined /> },
                            { title: 'Calendars', value: stats.calendars, icon: <CalendarOutlined /> },
                            { title: 'Stop Times', value: stats.stopTimes, icon: <FieldTimeOutlined /> }
                        ]}
                        renderItem={item => (
                            <List.Item>
                                <Card size="small">
                                    <Space>
                                        {item.icon}
                                        <span>{item.title}:</span>
                                        <Text strong>{item.value.toLocaleString()}</Text>
                                    </Space>
                                </Card>
                            </List.Item>
                        )}
                    />
                </Card>
            </Space>
        </div>
    );
};

const RouteVisualizer = ({ gtfsData }) => {
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [selectedTrip, setSelectedTrip] = useState(null);

    const handleRouteSelect = useCallback((routeId) => {
        if (!gtfsData) return;
        
        const route = gtfsData.routes.find(r => r.route_id === routeId);
        if (!route) return;

        setSelectedRoute(route);
        setSelectedTrip(null);
    }, [gtfsData]);

    const handleTripSelect = useCallback((tripId) => {
        if (!gtfsData) return;
        setSelectedTrip(tripId);
    }, [gtfsData]);

    const routeColumns = [
        {
            title: 'Property',
            dataIndex: 'property',
            key: 'property',
            width: '30%'
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value'
        }
    ];

    const getRouteDetails = () => {
        if (!selectedRoute) return [];
        
        const trips = gtfsData.trips.filter(t => t.route_id === selectedRoute.route_id);
        const uniqueServices = new Set(trips.map(t => t.service_id));
        
        return [
            { key: 'route_id', property: 'Route ID', value: selectedRoute.route_id },
            { key: 'route_short_name', property: 'Short Name', value: selectedRoute.route_short_name },
            { key: 'route_long_name', property: 'Long Name', value: selectedRoute.route_long_name },
            { key: 'route_type', property: 'Type', value: selectedRoute.route_type },
            { key: 'trips', property: 'Total Trips', value: trips.length },
            { key: 'services', property: 'Service Days', value: uniqueServices.size },
            { key: 'agency', property: 'Agency', value: selectedRoute.agency_id }
        ];
    };

    const getRouteStops = () => {
        if (!selectedTrip) return [];

        const stopTimes = gtfsData.stopTimes
            .filter(st => st.trip_id === selectedTrip)
            .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));

        return stopTimes.map(st => {
            const stop = gtfsData.stops.find(s => s.stop_id === st.stop_id);
            return {
                stop_id: stop?.stop_id,
                stop_name: stop?.stop_name,
                arrival_time: st.arrival_time,
                departure_time: st.departure_time,
                sequence: st.stop_sequence
            };
        });
    };

    if (!gtfsData) return null;

    const routeTrips = selectedRoute 
        ? gtfsData.trips.filter(t => t.route_id === selectedRoute.route_id)
        : [];

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Title level={4}>Route Explorer</Title>
                    <Text>
                        Explore your transit routes in detail. Select a route to view its trips and stops.
                        Each route contains multiple trips that follow the same path but at different times.
                    </Text>
                </Space>
            </Card>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Search and select a route to view its details"
                        optionFilterProp="children"
                        onChange={handleRouteSelect}
                        options={gtfsData.routes.map(route => ({
                            value: route.route_id,
                            label: `${route.route_short_name || ''} ${route.route_long_name || ''} (${route.route_id})`
                        }))}
                    />
                </Col>
            </Row>

            {selectedRoute && (
                <Row gutter={[16, 16]}>
                    <Col span={8}>
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <Card 
                                title={
                                    <Space>
                                        Route Details
                                        <Tooltip title="Basic information about the selected route">
                                            <QuestionCircleOutlined />
                                        </Tooltip>
                                    </Space>
                                }
                            >
                                <Table
                                    columns={routeColumns}
                                    dataSource={getRouteDetails()}
                                    pagination={false}
                                    size="small"
                                />
                            </Card>
                            <Card 
                                title={
                                    <Space>
                                        Route Trips
                                        <Tooltip title="Individual journeys scheduled for this route. Each trip represents a single journey from start to end">
                                            <QuestionCircleOutlined />
                                        </Tooltip>
                                    </Space>
                                }
                            >
                                <List
                                    size="small"
                                    dataSource={routeTrips}
                                    renderItem={trip => (
                                        <List.Item
                                            onClick={() => handleTripSelect(trip.trip_id)}
                                            style={{ 
                                                cursor: 'pointer',
                                                backgroundColor: selectedTrip === trip.trip_id ? '#f0f0f0' : 'transparent',
                                                padding: '12px',
                                                borderRadius: '4px',
                                                transition: 'background-color 0.3s'
                                            }}
                                            hoverable
                                        >
                                            <List.Item.Meta
                                                title={
                                                    <Space>
                                                        {trip.trip_short_name && <Tag color="blue">{trip.trip_short_name}</Tag>}
                                                        <Text strong>{trip.trip_headsign || 'No headsign'}</Text>
                                                    </Space>
                                                }
                                                description={
                                                    <Space>
                                                        <Text type="secondary">Trip ID: {trip.trip_id}</Text>
                                                        <Divider type="vertical" />
                                                        <Text type="secondary">Service: {trip.service_id}</Text>
                                                        {trip.direction_id !== undefined && (
                                                            <>
                                                                <Divider type="vertical" />
                                                                <Text type="secondary">Direction: {trip.direction_id}</Text>
                                                            </>
                                                        )}
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Space>
                    </Col>
                    <Col span={16}>
                        <Card 
                            title={
                                <Space>
                                    {selectedTrip ? "Trip Stops Sequence" : "Select a trip to view stops"}
                                    <Tooltip title="Shows the sequence of stops for the selected trip, including arrival and departure times">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Space>
                            }
                            bodyStyle={{ height: '600px', overflow: 'auto' }}
                        >
                            <Timeline mode="left">
                                {getRouteStops().map((stop, index) => (
                                    <Timeline.Item 
                                        key={stop.stop_id}
                                        dot={<EnvironmentOutlined style={{ fontSize: '16px' }} />}
                                        label={
                                            <Space>
                                                <Text strong>{stop.arrival_time}</Text>
                                                <ArrowRightOutlined />
                                                <Text strong>{stop.departure_time}</Text>
                                            </Space>
                                        }
                                    >
                                        <Space direction="vertical">
                                            <Text strong style={{ fontSize: '16px' }}>{stop.stop_name}</Text>
                                            <Space>
                                                <Tag color="blue">Stop ID: {stop.stop_id}</Tag>
                                                <Tag color="green">Sequence: {stop.sequence}</Tag>
                                            </Space>
                                        </Space>
                                    </Timeline.Item>
                                ))}
                            </Timeline>
                        </Card>
                    </Col>
                </Row>
            )}
        </Space>
    );
};

const StopVisualizer = ({ gtfsData }) => {
    const [searchText, setSearchText] = useState('');

    const getStopStats = useCallback((stop) => {
        if (!gtfsData?.stopTimes || !gtfsData?.trips) return { routes: 0, trips: 0 };

        const stopTimes = gtfsData.stopTimes.filter(st => st?.stop_id === stop.stop_id);
        const uniqueTrips = new Set(stopTimes.map(st => st?.trip_id).filter(Boolean));
        const uniqueRoutes = new Set(
            Array.from(uniqueTrips).map(tripId => 
                gtfsData.trips.find(t => t?.trip_id === tripId)?.route_id
            ).filter(Boolean)
        );

        return {
            routes: uniqueRoutes.size,
            trips: uniqueTrips.size
        };
    }, [gtfsData]);

    const filteredStops = useMemo(() => {
        if (!gtfsData?.stops) return [];
        return gtfsData.stops
            .filter(stop => 
                stop.stop_name?.toLowerCase().includes(searchText.toLowerCase()) ||
                stop.stop_id?.toLowerCase().includes(searchText.toLowerCase())
            )
            .map(stop => ({
                ...stop,
                ...getStopStats(stop)
            }));
    }, [gtfsData, searchText, getStopStats]);

    const stopColumns = [
        {
            title: 'Stop ID',
            dataIndex: 'stop_id',
            key: 'stop_id',
            width: '15%'
        },
        {
            title: 'Name',
            dataIndex: 'stop_name',
            key: 'stop_name',
            width: '25%'
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            width: '20%',
            render: (_, record) => `${record.stop_lat}, ${record.stop_lon}`
        },
        {
            title: 'Routes Served',
            dataIndex: 'routes',
            key: 'routes',
            width: '15%',
            sorter: (a, b) => a.routes - b.routes,
        },
        {
            title: 'Daily Trips',
            dataIndex: 'trips',
            key: 'trips',
            width: '15%',
            sorter: (a, b) => a.trips - b.trips,
        }
    ];

    if (!gtfsData?.stops) return null;

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Title level={4}>Stop Explorer</Title>
                    <Text>
                        View and search all stops in your transit network. Each stop shows its location, number of routes served, and total daily trips.
                        Click on column headers to sort by different metrics.
                    </Text>
                </Space>
            </Card>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder="Search stops by name or ID"
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                    />
                </Col>
            </Row>

            <Card>
                <Table
                    dataSource={filteredStops}
                    columns={stopColumns}
                    rowKey="stop_id"
                    size="small"
                    pagination={{ pageSize: 15 }}
                />
            </Card>
        </Space>
    );
};

const ScheduleVisualizer = ({ gtfsData }) => {
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedService, setSelectedService] = useState(null);

    const servicesByDate = useMemo(() => {
        if (!gtfsData?.calendar) return new Map();

        const serviceMap = new Map();
        gtfsData.calendar.forEach(service => {
            if (!service) return;
            
            const startDate = dayjs(service.start_date, 'YYYYMMDD');
            const endDate = dayjs(service.end_date, 'YYYYMMDD');
            let current = startDate;

            while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
                const dateKey = current.format('YYYY-MM-DD');
                const dayOfWeek = current.format('dddd').toLowerCase();
                
                if (service[dayOfWeek] === '1') {
                    if (!serviceMap.has(dateKey)) {
                        serviceMap.set(dateKey, new Set());
                    }
                    serviceMap.get(dateKey).add(service.service_id);
                }
                
                current = current.add(1, 'day');
            }
        });

        // Apply calendar_dates exceptions
        if (gtfsData.calendarDates) {
            gtfsData.calendarDates.forEach(exception => {
                if (!exception) return;
                
                const dateKey = dayjs(exception.date, 'YYYYMMDD').format('YYYY-MM-DD');
                if (!serviceMap.has(dateKey)) {
                    serviceMap.set(dateKey, new Set());
                }
                
                if (exception.exception_type === '1') { // Added service
                    serviceMap.get(dateKey).add(exception.service_id);
                } else if (exception.exception_type === '2') { // Removed service
                    serviceMap.get(dateKey).delete(exception.service_id);
                }
            });
        }

        return serviceMap;
    }, [gtfsData]);

    const getServiceDetails = (serviceId) => {
        if (!serviceId || !gtfsData?.calendar) return [];

        const service = gtfsData.calendar.find(s => s?.service_id === serviceId);
        if (!service) return [];

        const trips = gtfsData.trips?.filter(t => t?.service_id === serviceId) || [];
        const uniqueRoutes = new Set(trips.map(t => t?.route_id).filter(Boolean));

        const daysActive = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
            .filter(day => service[day] === '1')
            .map(day => day.charAt(0).toUpperCase() + day.slice(1))
            .join(', ');

        return [
            { key: 'service_id', property: 'Service ID', value: serviceId },
            { key: 'date_range', property: 'Date Range', value: `${dayjs(service.start_date, 'YYYYMMDD').format('YYYY-MM-DD')} to ${dayjs(service.end_date, 'YYYYMMDD').format('YYYY-MM-DD')}` },
            { key: 'days', property: 'Days Active', value: daysActive || 'None' },
            { key: 'routes', property: 'Routes', value: uniqueRoutes.size },
            { key: 'trips', property: 'Total Trips', value: trips.length }
        ];
    };

    const dateCellRender = (date) => {
        const dateKey = date.format('YYYY-MM-DD');
        const services = servicesByDate.get(dateKey);
        
        if (!services || services.size === 0) return null;

        return (
            <div className="service-badges">
                <Badge 
                    count={services.size} 
                    style={{ backgroundColor: '#52c41a' }}
                    title={`${services.size} services active`}
                />
            </div>
        );
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
        setSelectedService(null);
    };

    const handleServiceSelect = (serviceId) => {
        setSelectedService(serviceId);
    };

    const columns = [
        {
            title: 'Property',
            dataIndex: 'property',
            key: 'property',
            width: '30%'
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value'
        }
    ];

    if (!gtfsData) return null;

    const selectedDateKey = selectedDate.format('YYYY-MM-DD');
    const activeServices = Array.from(servicesByDate.get(selectedDateKey) || []);

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Title level={4}>Schedule Calendar</Title>
                    <Text>
                        View your transit service calendar. Green badges indicate the number of services running on each date.
                        Click on a date to view detailed service information.
                    </Text>
                </Space>
            </Card>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card 
                        title={
                            <Space>
                                Service Calendar
                                <Tooltip title="Click on a date to view services running on that day">
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </Space>
                        }
                    >
                        <Calendar
                            value={selectedDate}
                            onSelect={handleDateSelect}
                            cellRender={dateCellRender}
                            style={{ 
                                background: 'white', 
                                padding: '20px', 
                                borderRadius: '8px',
                                '.ant-picker-calendar-date-content': {
                                    height: '50px'
                                }
                            }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card 
                        title={
                            <Space>
                                <Text strong style={{ fontSize: '16px' }}>Services on {selectedDateKey}</Text>
                                <Badge 
                                    count={activeServices.length} 
                                    style={{ backgroundColor: '#52c41a' }}
                                />
                            </Space>
                        }
                    >
                        <List
                            dataSource={activeServices}
                            renderItem={serviceId => {
                                const service = gtfsData.calendar.find(s => s?.service_id === serviceId);
                                const tripCount = gtfsData.trips.filter(t => t.service_id === serviceId).length;
                                const daysActive = service ? ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                                    .filter(day => service[day] === '1')
                                    .map(day => day.charAt(0).toUpperCase() + day.slice(1))
                                    .join(', ') : '';
                                
                                return (
                                    <List.Item
                                        key={serviceId}
                                        onClick={() => handleServiceSelect(serviceId)}
                                        style={{ 
                                            cursor: 'pointer',
                                            padding: '16px',
                                            borderRadius: '8px',
                                            backgroundColor: selectedService === serviceId ? '#f0f0f0' : 'transparent',
                                            transition: 'background-color 0.3s',
                                            marginBottom: '8px',
                                            border: '1px solid #f0f0f0'
                                        }}
                                        hoverable
                                    >
                                        <Row style={{ width: '100%' }} align="middle">
                                            <Col span={6}>
                                                <Tag color="blue" style={{ padding: '4px 8px', fontSize: '14px' }}>
                                                    {serviceId}
                                                </Tag>
                                            </Col>
                                            <Col span={12}>
                                                <Text strong style={{ fontSize: '15px' }}>
                                                    {daysActive || 'Special Service'}
                                                </Text>
                                            </Col>
                                            <Col span={6} style={{ textAlign: 'right' }}>
                                                <Space>
                                                    <Tag color="green">
                                                        <Space>
                                                            <ClockCircleOutlined />
                                                            {tripCount} trips
                                                        </Space>
                                                    </Tag>
                                                </Space>
                                            </Col>
                                            {service && (
                                                <Col span={24} style={{ marginTop: '8px' }}>
                                                    <Text type="secondary">
                                                        <CalendarOutlined /> Valid: {dayjs(service.start_date, 'YYYYMMDD').format('MMM D')} - {dayjs(service.end_date, 'YYYYMMDD').format('MMM D, YYYY')}
                                                    </Text>
                                                </Col>
                                            )}
                                        </Row>
                                    </List.Item>
                                );
                            }}
                            locale={{ emptyText: 'No services on this date' }}
                        />
                    </Card>
                </Col>
                <Col span={16}>
                    {selectedService && (
                        <Card 
                            title={
                                <Space>
                                    <Text strong>Service Details</Text>
                                    <Tag color="blue" style={{ padding: '4px 8px' }}>{selectedService}</Tag>
                                </Space>
                            }
                            style={{ marginBottom: '16px' }}
                        >
                            <List
                                dataSource={getServiceDetails(selectedService)}
                                renderItem={item => (
                                    <List.Item>
                                        <Row style={{ width: '100%' }}>
                                            <Col span={10}>
                                                <Text type="secondary">{item.property}</Text>
                                            </Col>
                                            <Col span={14}>
                                                <Text strong>{item.value}</Text>
                                            </Col>
                                        </Row>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    )}
                </Col>
            </Row>
        </Space>
    );
};

const ServiceAnalysis = ({ gtfsData }) => {
    if (!gtfsData) return null;

    // Calculate service frequency by hour
    const frequencyByHour = Array(24).fill(0).map((_, hour) => ({
        hour,
        weekday: gtfsData.stopTimes.filter(st => {
            const tripService = gtfsData.trips.find(t => t.trip_id === st.trip_id);
            const service = tripService && gtfsData.calendar.find(s => s.service_id === tripService.service_id);
            return service && (service.monday === '1' || service.tuesday === '1' || service.wednesday === '1' || service.thursday === '1' || service.friday === '1') &&
                parseInt(st.arrival_time.split(':')[0]) === hour;
        }).length,
        weekend: gtfsData.stopTimes.filter(st => {
            const tripService = gtfsData.trips.find(t => t.trip_id === st.trip_id);
            const service = tripService && gtfsData.calendar.find(s => s.service_id === tripService.service_id);
            return service && (service.saturday === '1' || service.sunday === '1') &&
                parseInt(st.arrival_time.split(':')[0]) === hour;
        }).length
    }));

    // Calculate service gaps
    const serviceGaps = [];
    for (let hour = 0; hour < 24; hour++) {
        const weekdayTrips = frequencyByHour[hour].weekday;
        if (weekdayTrips < 5 && hour >= 6 && hour <= 22) {
            serviceGaps.push({
                hour,
                type: 'Weekday',
                trips: weekdayTrips
            });
        }
    }

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title="Service Frequency Analysis">
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Line
                            data={[
                                ...frequencyByHour.map(d => ({ hour: d.hour, trips: d.weekday, type: 'Weekday' })),
                                ...frequencyByHour.map(d => ({ hour: d.hour, trips: d.weekend, type: 'Weekend' }))
                            ]}
                            xField="hour"
                            yField="trips"
                            seriesField="type"
                            point={{}}
                            legend={{
                                position: 'top'
                            }}
                            xAxis={{
                                title: { text: 'Hour of Day' },
                                tickCount: 24
                            }}
                            yAxis={{
                                title: { text: 'Number of Trips' }
                            }}
                        />
                    </Col>
                </Row>
            </Card>

            <Card title="Service Gaps">
                <List
                    dataSource={serviceGaps}
                    renderItem={gap => (
                        <List.Item>
                            <Space>
                                <WarningOutlined style={{ color: '#faad14' }} />
                                <Text>Low service at {gap.hour}:00 ({gap.trips} trips/hour)</Text>
                            </Space>
                        </List.Item>
                    )}
                />
            </Card>
        </Space>
    );
};

const RouteAnalysis = ({ gtfsData }) => {
    if (!gtfsData) return null;

    // Calculate route overlaps
    const routeOverlaps = [];
    gtfsData.stops.forEach(stop => {
        const routesAtStop = new Set(
            gtfsData.stopTimes
                .filter(st => st.stop_id === stop.stop_id)
                .map(st => gtfsData.trips.find(t => t.trip_id === st.trip_id)?.route_id)
                .filter(Boolean)
        );

        if (routesAtStop.size > 1) {
            routeOverlaps.push({
                stop_name: stop.stop_name,
                routes: Array.from(routesAtStop).map(routeId => 
                    gtfsData.routes.find(r => r.route_id === routeId)?.route_short_name || routeId
                )
            });
        }
    });

    // Calculate route efficiency
    const routeEfficiency = gtfsData.routes.map(route => {
        const trips = gtfsData.trips.filter(t => t.route_id === route.route_id);
        const uniqueStops = new Set(
            trips.flatMap(trip => 
                gtfsData.stopTimes
                    .filter(st => st.trip_id === trip.trip_id)
                    .map(st => st.stop_id)
            )
        );

        return {
            route_name: route.route_short_name || route.route_id,
            stops: uniqueStops.size,
            trips: trips.length,
            efficiency: trips.length / uniqueStops.size
        };
    }).sort((a, b) => b.efficiency - a.efficiency);

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title="Route Overlaps">
                <List
                    dataSource={routeOverlaps}
                    renderItem={overlap => (
                        <List.Item>
                            <Space direction="vertical">
                                <Text strong>{overlap.stop_name}</Text>
                                <Space>
                                    {overlap.routes.map(route => (
                                        <Tag key={route} color="blue">{route}</Tag>
                                    ))}
                                </Space>
                            </Space>
                        </List.Item>
                    )}
                />
            </Card>

            <Card title="Route Efficiency">
                <Table
                    dataSource={routeEfficiency}
                    columns={[
                        { title: 'Route', dataIndex: 'route_name' },
                        { title: 'Stops', dataIndex: 'stops' },
                        { title: 'Trips', dataIndex: 'trips' },
                        { 
                            title: 'Efficiency Score', 
                            dataIndex: 'efficiency',
                            render: value => value.toFixed(2),
                            sorter: (a, b) => a.efficiency - b.efficiency
                        }
                    ]}
                />
            </Card>
        </Space>
    );
};

const StopAnalysis = ({ gtfsData }) => {
    if (!gtfsData) return null;

    // Calculate stop usage
    const stopUsage = gtfsData.stops.map(stop => {
        const visits = gtfsData.stopTimes.filter(st => st.stop_id === stop.stop_id).length;
        return {
            stop_name: stop.stop_name,
            visits,
            routes: new Set(
                gtfsData.stopTimes
                    .filter(st => st.stop_id === stop.stop_id)
                    .map(st => gtfsData.trips.find(t => t.trip_id === st.trip_id)?.route_id)
                    .filter(Boolean)
            ).size
        };
    }).sort((a, b) => b.visits - a.visits);

    // Calculate stop spacing
    const stopSpacing = [];
    gtfsData.routes.forEach(route => {
        const trips = gtfsData.trips.filter(t => t.route_id === route.route_id);
        trips.forEach(trip => {
            const stopSequence = gtfsData.stopTimes
                .filter(st => st.trip_id === trip.trip_id)
                .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));

            for (let i = 1; i < stopSequence.length; i++) {
                const prevStop = gtfsData.stops.find(s => s.stop_id === stopSequence[i-1].stop_id);
                const currStop = gtfsData.stops.find(s => s.stop_id === stopSequence[i].stop_id);
                
                if (prevStop && currStop) {
                    const distance = calculateDistance(
                        parseFloat(prevStop.stop_lat),
                        parseFloat(prevStop.stop_lon),
                        parseFloat(currStop.stop_lat),
                        parseFloat(currStop.stop_lon)
                    );

                    stopSpacing.push({
                        route: route.route_short_name || route.route_id,
                        from: prevStop.stop_name,
                        to: currStop.stop_name,
                        distance: distance.toFixed(2)
                    });
                }
            }
        });
    });

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title="Stop Usage Analysis">
                <Column
                    data={stopUsage.slice(0, 20)}
                    xField="stop_name"
                    yField="visits"
                    label={{
                        position: 'top',
                        style: {
                            fill: '#666'
                        }
                    }}
                    xAxis={{
                        label: {
                            autoRotate: true,
                            autoHide: true,
                            autoEllipsis: true
                        }
                    }}
                    yAxis={{
                        title: { text: 'Daily Visits' }
                    }}
                />
            </Card>

            <Card title="Stop Spacing Analysis">
                <Table
                    dataSource={stopSpacing}
                    columns={[
                        { title: 'Route', dataIndex: 'route' },
                        { title: 'From Stop', dataIndex: 'from' },
                        { title: 'To Stop', dataIndex: 'to' },
                        { 
                            title: 'Distance (km)', 
                            dataIndex: 'distance',
                            sorter: (a, b) => parseFloat(a.distance) - parseFloat(b.distance)
                        }
                    ]}
                />
            </Card>
        </Space>
    );
};

const ScheduleOptimization = ({ gtfsData }) => {
    if (!gtfsData) return null;

    // Calculate bunching
    const bunchingIssues = [];
    gtfsData.stops.forEach(stop => {
        const stopTimes = gtfsData.stopTimes
            .filter(st => st.stop_id === stop.stop_id)
            .map(st => ({
                ...st,
                route: gtfsData.trips.find(t => t.trip_id === st.trip_id)?.route_id
            }))
            .sort((a, b) => {
                const [aHour, aMin] = a.arrival_time.split(':').map(Number);
                const [bHour, bMin] = b.arrival_time.split(':').map(Number);
                return (aHour * 60 + aMin) - (bHour * 60 + bMin);
            });

        for (let i = 1; i < stopTimes.length; i++) {
            const [prevHour, prevMin] = stopTimes[i-1].arrival_time.split(':').map(Number);
            const [currHour, currMin] = stopTimes[i].arrival_time.split(':').map(Number);
            const timeDiff = (currHour * 60 + currMin) - (prevHour * 60 + prevMin);

            if (timeDiff < 5 && stopTimes[i-1].route === stopTimes[i].route) {
                bunchingIssues.push({
                    stop: stop.stop_name,
                    route: gtfsData.routes.find(r => r.route_id === stopTimes[i].route)?.route_short_name || stopTimes[i].route,
                    time: stopTimes[i].arrival_time,
                    gap: timeDiff
                });
            }
        }
    });

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title="Schedule Optimization">
                <Tabs items={[
                    {
                        key: 'bunching',
                        label: 'Service Bunching',
                        children: (
                            <List
                                dataSource={bunchingIssues}
                                renderItem={issue => (
                                    <List.Item>
                                        <Space>
                                            <WarningOutlined style={{ color: '#faad14' }} />
                                            <Text>Route {issue.route} at {issue.stop}: {issue.gap} minute gap at {issue.time}</Text>
                                        </Space>
                                    </List.Item>
                                )}
                            />
                        )
                    }
                ]} />
            </Card>
        </Space>
    );
};

// Helper function for distance calculation
function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
}

const GTFSInsights = ({ gtfsData }) => {
    const insights = useMemo(() => {
        if (!gtfsData) return [];

        // Calculate route metrics
        const routeMetrics = gtfsData.routes.map(route => {
            const trips = gtfsData.trips.filter(t => t.route_id === route.route_id);
            const stopTimes = trips.flatMap(trip => 
                gtfsData.stopTimes.filter(st => st.trip_id === trip.trip_id)
            );
            const uniqueStops = new Set(stopTimes.map(st => st.stop_id));
            
            return {
                route_id: route.route_id,
                route_name: route.route_short_name || route.route_long_name,
                trips_per_day: trips.length / (new Set(trips.map(t => t.service_id)).size || 1),
                unique_stops: uniqueStops.size,
                avg_trip_duration: stopTimes.length > 0 ? 
                    stopTimes.reduce((acc, st) => {
                        const [h, m] = st.arrival_time?.split(':').map(Number) || [0, 0];
                        return acc + (h * 60 + m);
                    }, 0) / trips.length : 0
            };
        });

        // Calculate stop utilization
        const stopUtilization = gtfsData.stops.map(stop => {
            const stopTimes = gtfsData.stopTimes.filter(st => st.stop_id === stop.stop_id);
            return {
                stop_id: stop.stop_id,
                stop_name: stop.stop_name,
                daily_visits: stopTimes.length,
                unique_routes: new Set(
                    stopTimes
                        .map(st => gtfsData.trips.find(t => t.trip_id === st.trip_id)?.route_id)
                        .filter(Boolean)
                ).size
            };
        });

        // Calculate time-based metrics
        const timeMetrics = {};
        gtfsData.stopTimes.forEach(st => {
            if (!st.arrival_time) return;
            const hour = parseInt(st.arrival_time.split(':')[0]);
            timeMetrics[hour] = (timeMetrics[hour] || 0) + 1;
        });

        const peakHours = Object.entries(timeMetrics)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3);

        const offPeakAvg = Object.entries(timeMetrics)
            .sort(([, a], [, b]) => a - b)
            .slice(0, Object.keys(timeMetrics).length - 3)
            .reduce((acc, [, count]) => acc + count, 0) / (Object.keys(timeMetrics).length - 3);

        return [
            {
                title: 'Network Performance Metrics',
                insights: [
                    `Network Coverage: ${routeMetrics.reduce((acc, r) => acc + r.unique_stops, 0)} total stop visits`,
                    `Average Route Length: ${(routeMetrics.reduce((acc, r) => acc + r.unique_stops, 0) / routeMetrics.length).toFixed(1)} stops`,
                    `Network Density: ${(stopUtilization.reduce((acc, s) => acc + s.unique_routes, 0) / gtfsData.stops.length).toFixed(1)} routes per stop`,
                    `Service Intensity: ${(gtfsData.stopTimes.length / (gtfsData.stops.length * 24)).toFixed(1)} average visits per stop per hour`
                ]
            },
            {
                title: 'Route Efficiency Analysis',
                insights: [
                    'Most efficient routes (trips/stops ratio):',
                    ...routeMetrics
                        .sort((a, b) => (b.trips_per_day / b.unique_stops) - (a.trips_per_day / a.unique_stops))
                        .slice(0, 5)
                        .map(r => `${r.route_name}: ${(r.trips_per_day / r.unique_stops).toFixed(1)} trips per stop`),
                    `Average trip duration: ${(routeMetrics.reduce((acc, r) => acc + r.avg_trip_duration, 0) / routeMetrics.length).toFixed(0)} minutes`,
                    `Routes with optimal coverage: ${routeMetrics.filter(r => r.unique_stops >= 10 && r.trips_per_day >= 10).length}`
                ]
            },
            {
                title: 'Stop Utilization Patterns',
                insights: [
                    'Most connected stops:',
                    ...stopUtilization
                        .sort((a, b) => b.unique_routes - a.unique_routes)
                        .slice(0, 5)
                        .map(s => `${s.stop_name}: ${s.unique_routes} routes, ${s.daily_visits} daily visits`),
                    `Transfer points (stops serving 3+ routes): ${stopUtilization.filter(s => s.unique_routes >= 3).length}`,
                    `Underutilized stops (< 5 daily visits): ${stopUtilization.filter(s => s.daily_visits < 5).length}`
                ]
            },
            {
                title: 'Time Distribution Analysis',
                insights: [
                    'Peak service hours:',
                    ...peakHours.map(([hour, count]) => 
                        `${hour}:00-${hour}:59: ${count} trips (${((count / Object.values(timeMetrics).reduce((a, b) => a + b, 0)) * 100).toFixed(1)}% of daily service)`
                    ),
                    `Peak vs Off-peak ratio: ${(peakHours[0][1] / offPeakAvg).toFixed(1)}x`,
                    `Service span: ${Math.min(...Object.keys(timeMetrics).map(Number))}:00 to ${Math.max(...Object.keys(timeMetrics).map(Number))}:00`
                ]
            }
        ];
    }, [gtfsData]);

    const handleExportInsights = () => {
        if (!gtfsData) return;

        const csvContent = insights.map(section => {
            return [
                section.title,
                ...section.insights
            ].join('\n');
        }).join('\n\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'gtfs_insights.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    if (!gtfsData) return null;

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Space direction="horizontal" align="center" justify="space-between" style={{ width: '100%' }}>
                        <Space direction="vertical">
                            <Title level={4}>Network Insights</Title>
                            <Text>
                                Comprehensive analysis of your transit network performance and efficiency metrics.
                            </Text>
                        </Space>
                        <Button 
                            type="primary" 
                            icon={<DatabaseOutlined />}
                            onClick={handleExportInsights}
                        >
                            Export Insights
                        </Button>
                    </Space>
                </Space>
            </Card>

            <Row gutter={[16, 16]}>
                {insights.map((section, index) => (
                    <Col key={index} span={12}>
                        <Card 
                            title={
                                <Space>
                                    {section.title}
                                    <Tooltip title="Click on any insight to copy to clipboard">
                                        <InfoCircleOutlined />
                                    </Tooltip>
                                </Space>
                            }
                            className="insight-card"
                            style={{ height: '100%' }}
                        >
                            <List
                                dataSource={section.insights}
                                renderItem={insight => (
                                    <List.Item
                                        onClick={() => {
                                            navigator.clipboard.writeText(insight);
                                            message.success('Copied to clipboard');
                                        }}
                                        style={{ 
                                            cursor: 'pointer',
                                            padding: '8px 12px',
                                            borderRadius: '4px',
                                            transition: 'background-color 0.3s'
                                        }}
                                        hoverable
                                    >
                                        <Typography.Text>
                                            {insight.startsWith('Most') || insight.startsWith('Peak') ? (
                                                <Text strong>{insight}</Text>
                                            ) : (
                                                <Text>{insight}</Text>
                                            )}
                                        </Typography.Text>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>
        </Space>
    );
};

// Add new GTFSInfo component after GTFSInsights
const GTFSInfo = () => {
    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Title level={4}>Understanding GTFS</Title>
                    <Text>
                        GTFS (General Transit Feed Specification) is a standardized format for public transportation schedules and associated geographic information.
                        It allows transit agencies to publish their data in a format that can be consumed by various applications.
                    </Text>
                </Space>
            </Card>

            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Card title="Core GTFS Files">
                        <List
                            dataSource={[
                                {
                                    title: 'agency.txt',
                                    description: 'Contains transit agency information including name, URL, timezone, etc.'
                                },
                                {
                                    title: 'routes.txt',
                                    description: 'Defines transit routes. Each route has a unique ID, short and long names, and type (bus, train, etc.).'
                                },
                                {
                                    title: 'trips.txt',
                                    description: 'Individual vehicle journeys. Links routes with their specific schedules and stop sequences.'
                                },
                                {
                                    title: 'stops.txt',
                                    description: 'All stop locations in the network with coordinates, names, and accessibility information.'
                                },
                                {
                                    title: 'stop_times.txt',
                                    description: 'Arrival and departure times for each stop in each trip.'
                                },
                                {
                                    title: 'calendar.txt',
                                    description: 'Service patterns defining which days of the week services operate.'
                                }
                            ]}
                            renderItem={item => (
                                <List.Item>
                                    <List.Item.Meta
                                        title={<Text strong>{item.title}</Text>}
                                        description={item.description}
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Optional GTFS Files">
                        <List
                            dataSource={[
                                {
                                    title: 'calendar_dates.txt',
                                    description: 'Exceptions to regular service patterns (holidays, special events).'
                                },
                                {
                                    title: 'shapes.txt',
                                    description: 'Geographic shape of routes as a sequence of points.'
                                },
                                {
                                    title: 'frequencies.txt',
                                    description: 'Headway-based service intervals for routes with regular frequency.'
                                },
                                {
                                    title: 'transfers.txt',
                                    description: 'Rules for transfers between routes at specific stops.'
                                }
                            ]}
                            renderItem={item => (
                                <List.Item>
                                    <List.Item.Meta
                                        title={<Text strong>{item.title}</Text>}
                                        description={item.description}
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
            </Row>

            <Card title="Key Concepts">
                <Row gutter={[16, 16]}>
                    <Col span={8}>
                        <Card title="Routes & Trips" size="small">
                            <Text>
                                A route represents a service line (e.g., "Route 100"), while trips are individual journeys along that route.
                                Multiple trips can operate on the same route at different times or in different directions.
                            </Text>
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card title="Services & Calendars" size="small">
                            <Text>
                                Services define when trips operate using calendar patterns (weekday, weekend, etc.).
                                Calendar dates can modify these patterns for special events or holidays.
                            </Text>
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card title="Stops & Times" size="small">
                            <Text>
                                Stops are physical locations where vehicles pick up or drop off passengers.
                                Stop times define when vehicles arrive at and depart from each stop during a trip.
                            </Text>
                        </Card>
                    </Col>
                </Row>
            </Card>

            <Card title="Data Relationships">
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>
                        GTFS files are related through common IDs:
                    </Text>
                    <ul>
                        <li>Routes belong to Agencies (agency_id)</li>
                        <li>Trips belong to Routes (route_id) and Services (service_id)</li>
                        <li>Stop Times link Trips (trip_id) with Stops (stop_id)</li>
                        <li>Shapes can be associated with Trips (shape_id)</li>
                    </ul>
                    <Alert
                        message="Best Practices"
                        description={
                            <ul>
                                <li>Keep IDs consistent and meaningful</li>
                                <li>Ensure all referenced IDs exist in their respective files</li>
                                <li>Maintain accurate stop sequences and times</li>
                                <li>Use calendar_dates.txt for service exceptions</li>
                            </ul>
                        }
                        type="info"
                        showIcon
                    />
                </Space>
            </Card>
        </Space>
    );
};

export default function Visualizer() {
    const [loading, setLoading] = useState(false);
    const [gtfsData, setGtfsData] = useState(null);
    const { operator } = useAppContext();
    const [activeTab, setActiveTab] = useState('overview');

    const handleLoadCurrentGTFS = async () => {
        if (!operator) {
            message.error('No operator found. Please make sure you are logged in and have selected an operator.');
            return;
        }

        setLoading(true);
        message.loading('Loading current GTFS data...', 0);

        try {
            const userId = operator.operatorKey;
            const schedules = values(await scheduleModelData.getAll());
            const stops = values(await stopModelData.getAll(userId)).filter(s => (!s.stopType || s.stopType === 'bus'));
            const stopsById = keyBy(stops, 'stopId');
            const routes = values(await routeModelData.getAll())
                .filter(r => r.published === 1)
                .map(r => new BusRoute(r));

            const shiftBats = await shiftBatModelData.getAll({asArray: true});
            const zip = await buildGtfsZip(operator, routes, schedules, stopsById,
                await driverShiftModelData.getAll(),
                await vehicleShiftModelData.getAll(),
                shiftBats,
                await deadrunModelData.getAll(),
                {exts: {tfnsw: false}, excludeInvalid: false}
            );

            const file = new File([zip.toBuffer()], 'current_gtfs.zip', { type: 'application/zip' });
            const gtfsProcessor = new GTFSProcessor();
            await gtfsProcessor.loadGTFSFile(file);
            
            setGtfsData(gtfsProcessor.data);
            message.destroy();
            message.success('Current GTFS data loaded successfully');
        } catch (err) {
            console.error('Error loading current GTFS:', err);
            message.destroy();
            message.error('Error loading current GTFS data: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const items = [
        {
            key: 'overview',
            label: 'Overview',
            children: <GTFSOverview gtfsData={gtfsData} />
        },
        {
            key: 'routes',
            label: 'Routes',
            children: <RouteVisualizer gtfsData={gtfsData} />
        },
        {
            key: 'stops',
            label: 'Stops',
            children: <StopVisualizer gtfsData={gtfsData} />
        },
        {
            key: 'schedules',
            label: 'Schedules',
            children: <ScheduleVisualizer gtfsData={gtfsData} />
        },
        {
            key: 'service-analysis',
            label: 'Service Analysis',
            children: <ServiceAnalysis gtfsData={gtfsData} />
        },
        {
            key: 'route-analysis',
            label: 'Route Analysis',
            children: <RouteAnalysis gtfsData={gtfsData} />
        },
        {
            key: 'stop-analysis',
            label: 'Stop Analysis',
            children: <StopAnalysis gtfsData={gtfsData} />
        },
        {
            key: 'schedule-optimization',
            label: 'Schedule Optimization',
            children: <ScheduleOptimization gtfsData={gtfsData} />
        },
        {
            key: 'insights',
            label: 'Insights',
            children: <GTFSInsights gtfsData={gtfsData} />
        },
        {
            key: 'info',
            label: 'Info',
            children: <GTFSInfo />
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Card>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Title level={3}>GTFS Data Visualizer</Title>
                        <Text>
                            This tool helps you explore and understand your transit data in the GTFS format.
                            Load your current GTFS data to view routes, stops, schedules, and network insights.
                        </Text>
                        <Space style={{ marginTop: '16px' }}>
                            <Button
                                icon={<DatabaseOutlined />}
                                onClick={handleLoadCurrentGTFS}
                                loading={loading}
                                type="primary"
                                size="large"
                            >
                                Load Current GTFS Data
                            </Button>
                            <Tooltip title="Click to load your current GTFS data and start exploring">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </Space>
                    </Space>
                </Card>

                {loading ? (
                    <Spin size="large" tip="Processing GTFS data..." />
                ) : gtfsData ? (
                    <Card>
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={items}
                            size="large"
                        />
                    </Card>
                ) : null}
            </Space>
        </div>
    );
}